import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ChevronRight } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import valveBannerImage from '../../../images/ValveService/ValveService4.jpeg';
import valve1 from '../../../images/ValveService/ValveService1.jpg';
import valve2 from '../../../images/ValveService/ValveService2.jpeg';
import valve3 from '../../../images/ValveService/ValveService3.jpeg';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './ValveService.css';

const ValveService = () => {
  const valveTypes = [
    'Gate Valve',
    'Globe Valve',
    'Check Valve',
    'Ball Valve',
    'Butterfly Valve',
    'Wafer Check Valve',
    'Knife Gate Valve',
    'Forged Steel Valve',
    'Y Type Strainer',
    'Basket Type Strainer',
    'Sluice Valves',
    'Foot Valve',
    'Needle Valve'
  ];

  const valveCategories = [
    'Safety Valves / Control Valves',
    'Automatic Recirculation Valve (ARC)',
    'Coker Ball Valves (4 Way)',
    'HP and LP Bypass Valves',
    'Boiler Feed Pump Valves',
    'Boiler Outlet Valves',
    'Drum Safety Valves',
    'Cryogenic Valves',
    'Turbine and Boiler Valves',
    'Gate / Globe / Swing Check Valves',
    'Ball Valves / Butterfly Valves / Forged Steel Valves',
    'Knife Gate Valves / Plug Valves',
    'All other valves not mentioned above'
  ];

  const features = [
    '150# - 4500# pressure class support',
    'All brands and sizes supported',
    'On-site and workshop repair services',
    'Portable Lapping Machine support available',
    'Workshop refurbishment services',
    'Reconditioning/refurbishment work',
    'Seat, Plug, Disc, Wedge rebuilding',
    'Comprehensive spare parts support',
    'Pan India service network'
  ];

  return (
    <div className="valve-service-page">
      {/* Hero Section */}
      <section className="valve-hero" style={{ backgroundImage: `url(${valveBannerImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="valve-hero-content"
          >
            <Breadcrumb />
            <h1 className="valve-hero-title">
              <span className="valve-hero-title-highlight">On-site</span> Valve Servicing
            </h1>
            <p className="valve-hero-description">
              Complete valve repair services ranging from 150# to 4500# pressure class. 
              Expert refurbishment and maintenance for all valve types.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="valve-overview">
        <div className="container-custom">
          <div className="valve-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="valve-overview-content"
            >
              <h2 className="valve-overview-title">Comprehensive Valve Solutions</h2>
              <p className="valve-overview-description">
                We repair all brands and sizes of control and isolation valves ranging from 150# to 4500# pressure class. 
                Our extensive library of manufacturer specifications ensures proper repair and maintenance 
                with workshop support for refurbishment work.
              </p>
              <div className="valve-features">
                {features.map((feature, index) => (
                  <div key={index} className="valve-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="valve-overview-visual"
            >
              <div className="valve-image-gallery">
                {[
                  { src: valve1, alt: 'Valve Service', title: 'Comprehensive Solutions' },
                  { src: valve2, alt: 'Valve Repair', title: 'Expert Refurbishment' },
                  { src: valve3, alt: 'Valve Quality', title: 'Quality Service' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="valve-image-card"
                  >
                    <div className="valve-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="valve-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valves We Service - List Section */}
      <section className="valve-types-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="valve-types-header"
          >
            <h2 className="valve-types-main-title">Valves We Service</h2>
            <p className="valve-types-main-description">
              Comprehensive coverage across valve types, strainers and specialized applications
            </p>
          </motion.div>
          <div className="valve-types-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="valve-types-card"
            >
              <h3 className="valve-types-card-title">Valve & Strainer Types</h3>
              <ul className="valve-types-list">
                {valveTypes.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                    className="valve-types-list-item"
                  >
                    <ChevronRight className="valve-types-chevron" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="valve-types-card"
            >
              <h3 className="valve-types-card-title">Applications & Specialized Valves</h3>
              <ul className="valve-types-list">
                {valveCategories.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                    className="valve-types-list-item"
                  >
                    <ChevronRight className="valve-types-chevron" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brands Section - Generic */}
      <section className="valve-brands">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="valve-brands-header"
          >
            <h2 className="valve-brands-title">We Work With All Brands</h2>
            <p className="valve-brands-description">
              We service all major control and isolation valve manufacturers. Our team has the expertise and specifications to repair and maintain equipment from any brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="valve-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="valve-cta-content"
          >
            <h2 className="valve-cta-title">Need Valve Maintenance?</h2>
            <p className="valve-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            
            {/* Enquiry Form */}
            <div className="valve-cta-form">
              <EnquiryForm serviceName="Control Valve" />
            </div>
            
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ValveService;
